import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiShield, FiActivity, FiLock, FiCheckCircle, FiAlertCircle, FiDatabase, FiFileText, FiClock } = FiIcons;

const Compliance = () => {
  const stats = [
    { label: "HIPAA Compliance", status: "Verified", color: "text-green-500" },
    { label: "FDA CDS Status", status: "Class II Ready", color: "text-brand-indigo" },
    { label: "Data Residency", status: "USA (US-East-1)", color: "text-brand-indigo" },
    { label: "Audit Log Integrity", status: "100% Immutable", color: "text-green-500" }
  ];

  const logs = [
    { id: 1, event: "BAA Signature Verified", org: "Memorial Health", time: "2h ago", type: "Legal" },
    { id: 2, event: "Workflow Validation Passed", org: "Chest Pain Triage", time: "5h ago", type: "Security" },
    { id: 3, event: "Encryption Key Rotation", org: "HDAI Core", time: "1d ago", type: "System" },
    { id: 4, event: "New Clinician Verified", org: "Dr. Sarah J.", time: "2d ago", type: "Auth" }
  ];

  return (
    <div className="min-h-screen bg-brand-ivory pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-16">
          <div className="inline-flex items-center space-x-2 bg-brand-mint/10 text-brand-indigo px-4 py-2 rounded-full mb-6 border border-brand-mint/20">
            <SafeIcon icon={FiShield} />
            <span className="text-xs font-bold uppercase tracking-widest">Compliance Command Center</span>
          </div>
          <h1 className="text-5xl font-display font-bold text-brand-indigo mb-4">Governance & Audit</h1>
          <p className="text-xl text-brand-slate max-w-2xl">Real-time transparency into your clinical AI operations. Every inference, every edit, every BAA—logged and verifiable.</p>
        </div>

        <div className="grid lg:grid-cols-4 gap-8 mb-12">
          {stats.map((s, i) => (
            <motion.div 
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="bg-white p-8 rounded-[2rem] border border-gray-100 shadow-sm"
            >
              <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">{s.label}</div>
              <div className={`text-xl font-bold ${s.color} flex items-center space-x-2`}>
                <SafeIcon icon={FiCheckCircle} />
                <span>{s.status}</span>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            {/* Audit Log */}
            <div className="bg-white rounded-[3rem] p-10 border border-gray-100 shadow-sm">
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-2xl font-display font-bold text-brand-indigo">Immutable Audit Trail</h3>
                <button className="flex items-center space-x-2 text-brand-indigo text-sm font-bold">
                  <SafeIcon icon={FiFileText} />
                  <span>Export Report</span>
                </button>
              </div>
              <div className="space-y-4">
                {logs.map((log) => (
                  <div key={log.id} className="flex items-center justify-between p-6 bg-brand-ivory rounded-2xl border border-gray-100 hover:border-brand-mint transition-all">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm">
                        <SafeIcon icon={FiActivity} className="text-brand-indigo" />
                      </div>
                      <div>
                        <div className="font-bold text-brand-indigo">{log.event}</div>
                        <div className="text-xs text-brand-slate">{log.org}</div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-6">
                      <div className="text-[10px] font-bold px-3 py-1 bg-white rounded-full border border-gray-100 uppercase text-gray-400">{log.type}</div>
                      <div className="text-xs font-bold text-brand-indigo flex items-center space-x-2">
                        <SafeIcon icon={FiClock} />
                        <span>{log.time}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-8">
            <div className="bg-brand-indigo rounded-[3rem] p-10 text-white relative overflow-hidden">
              <div className="relative z-10">
                <div className="w-14 h-14 bg-brand-mint rounded-2xl flex items-center justify-center mb-8">
                  <SafeIcon icon={FiLock} className="text-brand-indigo text-2xl" />
                </div>
                <h3 className="text-2xl font-display font-bold mb-4">Patient Vault Storage</h3>
                <p className="text-white/60 mb-8 leading-relaxed">All PHI is stored in zero-knowledge encrypted vaults. HDAI does not hold your decryption keys.</p>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3 text-sm font-bold">
                    <SafeIcon icon={FiCheckCircle} className="text-brand-mint" />
                    <span>AES-256-GCM Encryption</span>
                  </div>
                  <div className="flex items-center space-x-3 text-sm font-bold">
                    <SafeIcon icon={FiCheckCircle} className="text-brand-mint" />
                    <span>SOC2 Type II Complaint</span>
                  </div>
                </div>
              </div>
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32" />
            </div>

            <div className="bg-white p-10 rounded-[3rem] border border-gray-100 shadow-sm text-center">
              <SafeIcon icon={FiAlertCircle} className="text-4xl text-brand-indigo mx-auto mb-6" />
              <h4 className="font-display font-bold text-brand-indigo mb-2">Need a Custom BAA?</h4>
              <p className="text-sm text-brand-slate mb-8">Enterprise customers can request custom HIPAA Business Associate Agreements.</p>
              <button className="w-full py-4 bg-brand-ivory text-brand-indigo font-bold rounded-2xl hover:bg-brand-indigo hover:text-white transition-all">
                Contact Legal
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Compliance;